import React, { useMemo, useState } from 'react';
import { NamedLink } from '../../components';
import { trackGA4Event } from '../../analytics/ga4';
import WorkshopNewsletterBand from './WorkshopNewsletterBand';
import css from './WorkshopPage.module.css';

/**
 * Shared clean collection view for the Workshop category + trailer-type pages.
 * Clean white/orange skin (mockup #2): breadcrumb -> 2-col hero -> sub-category
 * pills -> [filter sidebar | 3-col product grid + sort] -> guide callout.
 *
 * Facets are driven ONLY by data that actually exists on the accessories:
 *   - goals        -> sub-category pills + "Use" sidebar group
 *   - trailerTypes -> "Trailer type" sidebar group
 *   - brand        -> "Brand" sidebar group
 * No invented facets (e.g. working-load-limit) — those aren't reliably in Strapi.
 *
 * Props:
 *   accessories  [{...mapAccessory}]
 *   crumbs       [{ name, to:{name,params} } | { name }]   (last = current)
 *   title        string
 *   subtitle     string      (one-line lede under the H1)
 *   heroBody     string      (optional supporting paragraph under the subtitle)
 *   heroImg      imported asset | url | null
 *   source       'category_page' | 'type_page'  (GA4 context)
 *   contextSlug  slug (GA4 item_category / trailer_type)
 *
 * The old "Not sure which X you need? / Read the owner guides" guide callout
 * was removed 2026-10-12 (we don't publish spec/fitment guides) and replaced
 * with The Weekly Load newsletter band.
 */

const SORTS = [
  { key: 'featured', label: 'Featured' },
  { key: 'price-asc', label: 'Price: Low to High' },
  { key: 'price-desc', label: 'Price: High to Low' },
  { key: 'name', label: 'Name: A to Z' },
];

const fireAffiliate = (product, source, contextSlug) =>
  trackGA4Event('click_affiliate', {
    item_id: product.asin || product.slug,
    item_name: product.name,
    item_brand: product.brand || undefined,
    item_category: product.category ? product.category.slug : contextSlug,
    price: product.priceValue || undefined,
    affiliate_network: product.affiliateNetwork || 'amazon',
    source,
  });

const ProductCard = ({ product, source, contextSlug }) => (
  <article className={css.collCard}>
    <NamedLink
      name="WorkshopProductPage"
      params={{ slug: product.slug }}
      className={css.collCardImg}
    >
      {product.image ? (
        <img src={product.image} alt={product.name} loading="lazy" />
      ) : (
        <span className={css.ph} style={{ minHeight: 0 }}>
          {product.category ? product.category.name : 'Gear'}
        </span>
      )}
    </NamedLink>
    <div className={css.collCardBody}>
      {product.kicker || product.category ? (
        <div className={css.collCardKicker}>
          {(product.kicker || (product.category && product.category.name) || '').toString()}
        </div>
      ) : null}
      <h3 className={css.collCardName}>
        <NamedLink name="WorkshopProductPage" params={{ slug: product.slug }}>
          {product.name}
        </NamedLink>
      </h3>
      {product.description ? (
        <p className={css.collCardDesc}>{product.description}</p>
      ) : null}
      <div className={css.collCardFoot}>
        <NamedLink
          name="WorkshopProductPage"
          params={{ slug: product.slug }}
          className={css.collCardView}
        >
          View details <span className={css.arrow}>→</span>
        </NamedLink>
        {product.affiliateUrl ? (
          <a
            className={css.collCardAmz}
            href={product.affiliateUrl}
            target="_blank"
            rel="nofollow sponsored noopener noreferrer"
            onClick={() => fireAffiliate(product, source, contextSlug)}
          >
            Available on {product.retailerLabel || 'Amazon'} ↗
          </a>
        ) : null}
      </div>
    </div>
  </article>
);

const FilterGroup = ({ title, options, selected, onToggle }) => {
  const [open, setOpen] = useState(true);
  if (!options || !options.length) return null;
  return (
    <div className={css.filterGroup2}>
      <button
        type="button"
        className={`${css.filterGroupHd} ${open ? '' : css.collapsed}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        {title}
        <span className={css.chev} aria-hidden="true">▾</span>
      </button>
      {open ? (
        <div>
          {options.map(opt => (
            <label key={opt.key} className={css.filterOpt}>
              <input
                type="checkbox"
                checked={selected.includes(opt.key)}
                onChange={() => onToggle(opt.key)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const WorkshopCollection = ({
  accessories = [],
  crumbs = [],
  title,
  subtitle,
  heroBody,
  heroImg,
  source = 'collection_page',
  contextSlug,
}) => {
  const [activeGoal, setActiveGoal] = useState(null); // top pills
  const [typeFilter, setTypeFilter] = useState([]);
  const [brandFilter, setBrandFilter] = useState([]);
  const [useFilter, setUseFilter] = useState([]); // sidebar goals
  const [catFilter, setCatFilter] = useState([]); // sidebar category (type page)
  const [sort, setSort] = useState('featured');

  // ── Real facets derived from the data ──────────────────────────────────────
  const goalFacets = useMemo(() => {
    const m = new Map();
    accessories.forEach(a => (a.goals || []).forEach(g => m.set(g.slug || g.name, g.name)));
    return [...m.entries()].map(([key, label]) => ({ key, label }));
  }, [accessories]);

  const typeFacets = useMemo(() => {
    const m = new Map();
    accessories.forEach(a => (a.trailerTypes || []).forEach(t => m.set(t.slug, t.name)));
    return [...m.entries()].map(([key, label]) => ({ key, label }));
  }, [accessories]);

  const brandFacets = useMemo(() => {
    const s = new Set();
    accessories.forEach(a => a.brand && s.add(a.brand));
    return [...s].sort().map(b => ({ key: b, label: b }));
  }, [accessories]);

  // Category facet only matters when the result set spans >1 category
  // (i.e. trailer-type pages). On a single-category page this stays empty/hidden.
  const catFacets = useMemo(() => {
    const m = new Map();
    accessories.forEach(a => a.category && m.set(a.category.slug, a.category.name));
    const arr = [...m.entries()].map(([key, label]) => ({ key, label }));
    return arr.length > 1 ? arr.sort((a, b) => a.label.localeCompare(b.label)) : [];
  }, [accessories]);

  const toggle = (val, list, setter) =>
    setter(list.includes(val) ? list.filter(x => x !== val) : [...list, val]);

  // ── Filter + sort ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...accessories];
    if (activeGoal)
      list = list.filter(a => (a.goals || []).some(g => (g.slug || g.name) === activeGoal));
    if (useFilter.length)
      list = list.filter(a => (a.goals || []).some(g => useFilter.includes(g.slug || g.name)));
    if (typeFilter.length)
      list = list.filter(a => (a.trailerTypes || []).some(t => typeFilter.includes(t.slug)));
    if (catFilter.length)
      list = list.filter(a => a.category && catFilter.includes(a.category.slug));
    if (brandFilter.length) list = list.filter(a => brandFilter.includes(a.brand));

    switch (sort) {
      case 'price-asc':
        list.sort((a, b) => (a.priceValue || 1e9) - (b.priceValue || 1e9));
        break;
      case 'price-desc':
        list.sort((a, b) => (b.priceValue || 0) - (a.priceValue || 0));
        break;
      case 'name':
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'featured':
      default:
        list.sort(
          (a, b) =>
            (b.crewPick ? 1 : 0) - (a.crewPick ? 1 : 0) ||
            (a.rank || 99) - (b.rank || 99) ||
            (a.order || 0) - (b.order || 0)
        );
        break;
    }
    return list;
  }, [accessories, activeGoal, useFilter, typeFilter, catFilter, brandFilter, sort]);

  const anyFilter =
    typeFilter.length || brandFilter.length || useFilter.length || catFilter.length;
  const clearAll = () => {
    setTypeFilter([]);
    setBrandFilter([]);
    setUseFilter([]);
    setCatFilter([]);
  };

  return (
    <div className={css.page}>
      {/* Breadcrumb */}
      <nav className={css.breadcrumb} aria-label="Breadcrumb">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <React.Fragment key={i}>
              {i > 0 ? <span className={css.sep}>/</span> : null}
              {last || !c.to ? (
                <span className={css.current}>{c.name}</span>
              ) : (
                <NamedLink name={c.to.name} params={c.to.params}>
                  {c.name}
                </NamedLink>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Page hero */}
      <section className={css.collHero}>
        <div>
          <h1 className={css.collHeroTitle}>{title}</h1>
          {subtitle ? <p className={css.collHeroSub}>{subtitle}</p> : null}
          {heroBody ? <p className={css.collHeroBody}>{heroBody}</p> : null}
          <div className={css.collHeroStats}>
            <span className={css.collHeroStat}>
              <strong>{accessories.length}</strong> {accessories.length === 1 ? 'product' : 'products'}
            </span>
            <span className={css.collHeroStatDot} aria-hidden="true">·</span>
            <span className={css.collHeroStat}>Owner-tested picks</span>
            <span className={css.collHeroStatDot} aria-hidden="true">·</span>
            <span className={css.collHeroStat}>Buy direct, same price</span>
          </div>
        </div>
        <div className={css.collHeroImg}>
          {heroImg ? (
            <img src={heroImg} alt={title} />
          ) : (
            <span className={css.ph}>{title}</span>
          )}
        </div>
      </section>

      {/* Sub-category pills (from goals) */}
      {goalFacets.length ? (
        <div className={css.pills}>
          <button
            type="button"
            className={`${css.pill} ${!activeGoal ? css.pillOn : ''}`}
            onClick={() => setActiveGoal(null)}
          >
            All gear
          </button>
          {goalFacets.map(g => (
            <button
              key={g.key}
              type="button"
              className={`${css.pill} ${activeGoal === g.key ? css.pillOn : ''}`}
              onClick={() => setActiveGoal(activeGoal === g.key ? null : g.key)}
            >
              {g.label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Body: filter sidebar + results */}
      <div className={css.collBody}>
        <aside className={css.filterCard}>
          <div className={css.filterCardTitle}>Filter gear</div>
          <FilterGroup
            title="Category"
            options={catFacets}
            selected={catFilter}
            onToggle={v => toggle(v, catFilter, setCatFilter)}
          />
          <FilterGroup
            title="Trailer type"
            options={typeFacets}
            selected={typeFilter}
            onToggle={v => toggle(v, typeFilter, setTypeFilter)}
          />
          <FilterGroup
            title="Use"
            options={goalFacets}
            selected={useFilter}
            onToggle={v => toggle(v, useFilter, setUseFilter)}
          />
          <FilterGroup
            title="Brand"
            options={brandFacets}
            selected={brandFilter}
            onToggle={v => toggle(v, brandFilter, setBrandFilter)}
          />
          {anyFilter ? (
            <div className={css.filterGroup2}>
              <button type="button" className={css.filterClear} onClick={clearAll}>
                Clear all filters
              </button>
            </div>
          ) : null}
        </aside>

        <div>
          <div className={css.resultsHd}>
            <span className={css.resultsCount}>
              {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
            </span>
            <div className={css.sortWrap}>
              <span className={css.sortLabel}>Sort</span>
              <select
                className={css.sortSel}
                value={sort}
                onChange={e => setSort(e.target.value)}
                aria-label="Sort products"
              >
                {SORTS.map(s => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {filtered.length ? (
            <div className={css.collGrid}>
              {filtered.map(p => (
                <ProductCard
                  key={p.slug}
                  product={p}
                  source={source}
                  contextSlug={contextSlug}
                />
              ))}
            </div>
          ) : (
            <div className={css.collEmpty}>
              No gear matches those filters.{' '}
              {anyFilter ? (
                <button type="button" className={css.filterClear} onClick={clearAll}>
                  Clear filters
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* The Weekly Load newsletter band (replaced the old owner-guides callout) */}
      <div className={css.newsletterBandWrap}>
        <WorkshopNewsletterBand source={`weekly-load-${source}`} />
      </div>
    </div>
  );
};

export default WorkshopCollection;
